import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quizzes } from '@/api/quizzes';
import type { QuestionRow, CreateQuestionBody } from '@/api/quizzes';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { formatRef } from '@/lib/formatRef';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Plus, Trash2, CheckCircle2, GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function QuizDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<QuestionRow | null>(null);

  const { data: quiz, isLoading } = useQuery({
    queryKey: ['quiz', id],
    queryFn: () => quizzes.get(id!),
    enabled: Boolean(id),
  });
  const { data: questionsData } = useQuery({
    queryKey: ['quizQuestions', id],
    queryFn: () => quizzes.listQuestions(id!),
    enabled: Boolean(id),
  });
  const questions = questionsData?.rows ?? [];

  const removeQ = useMutation({
    mutationFn: (qid: string) => quizzes.deleteQuestion(id!, qid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quizQuestions', id] }),
  });

  if (!id) return <div className="text-sm text-destructive">Invalid URL.</div>;
  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!quiz) return <div className="text-sm text-destructive">Quiz not found.</div>;

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/quizzes" className="hover:underline">← Quizzes</Link>}
        title={quiz.name}
        description={quiz.description ?? `${formatRef(quiz.ref_number, 'QZ')} · Pass threshold ${quiz.pass_threshold_pct}% · ${quiz.points_on_pass} points on pass`}
        actions={canMutate && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add question
          </Button>
        )}
      />

      <div className="space-y-4 max-w-3xl">
        {questions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No questions yet.</p>
              {canMutate && (
                <Button size="sm" className="mt-4" onClick={() => setAddOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Add the first question
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          questions.map((q, i) => (
            <Card key={q.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
                      <GripVertical className="h-3.5 w-3.5" />
                      Question {i + 1} · {q.points} pt{q.points === 1 ? '' : 's'}
                    </div>
                    <CardTitle className="text-base font-medium">{q.question_text}</CardTitle>
                  </div>
                  {canMutate && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(q)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => removeQ.mutate(q.id)} disabled={removeQ.isPending}>
                        <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {q.choices.map((c) => (
                    <li key={c.key} className={cn(
                      'flex items-center gap-2 text-sm rounded-md border px-3 py-2',
                      c.key === q.correct_key
                        ? 'border-emerald-200 bg-emerald-50/60'
                        : 'border-border',
                    )}>
                      <span className="font-mono text-xs font-semibold text-muted-foreground w-6">{c.key}</span>
                      <span className="flex-1">{c.text}</span>
                      {c.key === q.correct_key && (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Quiz settings</CardTitle>
            <CardDescription>Configure scoring, points on pass, and admin notifications.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between py-1 border-b border-border">
              <span className="text-muted-foreground">Pass threshold</span>
              <span className="font-mono">{quiz.pass_threshold_pct}%</span>
            </div>
            <div className="flex justify-between py-1 border-b border-border">
              <span className="text-muted-foreground">Points on pass</span>
              <span className="font-mono">{quiz.points_on_pass}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Admin email</span>
              <span className="font-mono text-xs">{quiz.admin_email ?? '—'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {addOpen && <QuestionDialog
        quizId={id} onClose={() => setAddOpen(false)}
        defaultSortOrder={questions.length}
      />}
      {editTarget && <QuestionDialog
        quizId={id} onClose={() => setEditTarget(null)}
        existing={editTarget}
      />}
    </>
  );
}

function QuestionDialog({
  quizId, onClose, existing, defaultSortOrder,
}: {
  quizId: string;
  onClose: () => void;
  existing?: QuestionRow;
  defaultSortOrder?: number;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(existing?.question_text ?? '');
  const [points, setPoints] = useState(existing?.points ?? 1);
  const [choices, setChoices] = useState(existing?.choices ?? [
    { key: '1', text: '' },
    { key: '2', text: '' },
  ]);
  const [correctKey, setCorrectKey] = useState(existing?.correct_key ?? '1');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: CreateQuestionBody = {
        question_text: text.trim(),
        choices: choices.filter((c) => c.text.trim()),
        correct_key: correctKey,
        points,
        sort_order: existing?.sort_order ?? defaultSortOrder ?? 0,
      };
      return existing
        ? quizzes.updateQuestion(quizId, existing.id, body)
        : quizzes.createQuestion(quizId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quizQuestions', quizId] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  function updateChoice(i: number, field: 'key' | 'text', value: string) {
    setChoices((prev) => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  }
  function addChoice() {
    if (choices.length >= 9) return;
    const nextKey = String(choices.length + 1);
    setChoices((prev) => [...prev, { key: nextKey, text: '' }]);
  }
  function removeChoice(i: number) {
    setChoices((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      // If we removed the correct answer, fall back to the first remaining.
      if (!next.some((c) => c.key === correctKey) && next[0]) setCorrectKey(next[0].key);
      return next;
    });
  }

  function submit() {
    setError(null);
    if (!text.trim()) { setError('Question text required.'); return; }
    const filled = choices.filter((c) => c.text.trim());
    if (filled.length < 2) { setError('At least 2 choices required.'); return; }
    if (!filled.some((c) => c.key === correctKey)) { setError('Correct answer must be one of the choices.'); return; }
    save.mutate();
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit question' : 'New question'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="q-text">Question text (also used for TTS fallback)</Label>
            <Input id="q-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="What is the capital of France?" />
          </div>
          <div className="space-y-2">
            <Label>Choices — caller presses the key to answer</Label>
            {choices.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="correct" checked={correctKey === c.key}
                    onChange={() => setCorrectKey(c.key)}
                    className="accent-primary"
                  />
                  <span className="text-xs text-muted-foreground">Correct</span>
                </label>
                <Input
                  value={c.key} maxLength={1}
                  onChange={(e) => updateChoice(i, 'key', e.target.value.replace(/[^0-9*#]/g, '').slice(0, 1))}
                  className="w-14 font-mono text-sm text-center"
                  placeholder="Key"
                />
                <Input
                  value={c.text}
                  onChange={(e) => updateChoice(i, 'text', e.target.value)}
                  placeholder={`Choice ${i + 1}`}
                  className="flex-1"
                />
                {choices.length > 2 && (
                  <Button size="sm" variant="ghost" onClick={() => removeChoice(i)}>
                    <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
            {choices.length < 9 && (
              <Button size="sm" variant="outline" onClick={addChoice}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add choice
              </Button>
            )}
          </div>
          <div className="space-y-1 max-w-[180px]">
            <Label htmlFor="q-points">Points (per correct answer)</Label>
            <Input id="q-points" type="number" min={0} max={1000} value={points}
              onChange={(e) => setPoints(parseInt(e.target.value, 10) || 0)} />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={save.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Add question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
